import * as React from 'react';

import { get as userGet } from 'js/lib/user';

import { useConsumerMyPageHeader } from 'bundles/composable-page-header-consumer/contexts/ConsumerMyPageHeaderContext';
import DegreeLearnerOnConsumerDesktopMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/desktop/DegreeLearnerOnConsumerDesktopMainNav';
import EnterpriseLearnerOnConsumerDesktopMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/desktop/EnterpriseLearnerOnConsumerDesktopMainNav';
import LoggedInConsumerNoSubNavDesktopMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/desktop/LoggedInConsumerNoSubNavDesktopMainNav';
import LoggedOutConsumerDesktopMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/desktop/LoggedOutConsumerDesktopMainNav';
import OrgScopedEnterpriseLearnerOnConsumerDesktopMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/desktop/OrgScopedEnterpriseLearnerOnConsumerDesktopMainNav';
import DegreeLearnerOnConsumerMobileMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/mobile/DegreeLearnerOnConsumerMobileMainNav';
import EnterpriseLearnerOnConsumerMobileMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/mobile/EnterpriseLearnerOnConsumerMobileMainNav';
import LoggedInConsumerWithMetaNavLinksMobileMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/mobile/LoggedInConsumerWithMetaNavLinksMobileMainNav';
import LoggedOutConsumerMobileMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/mobile/LoggedOutConsumerMobileMainNav';
import OrgScopedEnterpriseLearnerOnConsumerMobileMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/mobile/OrgScopedEnterpriseLearnerOnConsumerMobileMainNav';
import DefaultConsumerPageHeaderProvider from 'bundles/composable-page-header-consumer/providers/DefaultConsumerPageHeaderProvider';
import GlobalPageHeaderContainer from 'bundles/composable-page-header/components/GlobalPageHeaderContainer';
import HeaderContextSwitcher from 'bundles/composable-page-header/components/helpers/HeaderContextSwitcher';
import DefaultMetaNavigation from 'bundles/composable-page-header/components/meta-nav/DefaultMetaNavigation';
import { DEFAULT_META_NAV_HEIGHT } from 'bundles/composable-page-header/constants';
import GlobalPageHeaderStateProvider from 'bundles/composable-page-header/providers/GlobalPageHeaderStateProvider';
import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';

type Props = {
  // A list of banner components that this page header may display. Name is used for key.
  banners?: Array<{
    name: string;
    component: React.ReactNode;
  }>;
  // Either hides or shows the meta nav. By default, it is only shown to logged-out users only.
  showMetaNav?: boolean;
  // Shows and hides the search bar and explore menus. By default, we show both the search and explore menus.
  showDiscoveryElements?: boolean;
  // Sets the header static to the top of the page, allowing it to scroll away. By default, this is false, and allows
  // the header to scroll with the learner as they scroll down the page.
  isPositionStatic?: boolean;
};

const DefaultHeader = ({ banners, showMetaNav, showDiscoveryElements, isPositionStatic }: Props) => {
  const { chosenEnterpriseProgram, chosenDegree } = useConsumerMyPageHeader();
  const user = userGet();

  const isOrgScoped = GrowthDiscoveryEpicClient.preview('orgScopedHome');
  const enterpriseHeader = isOrgScoped ? (
    <OrgScopedEnterpriseLearnerOnConsumerDesktopMainNav showDiscoveryElements={showDiscoveryElements} />
  ) : (
    <EnterpriseLearnerOnConsumerDesktopMainNav showDiscoveryElements={showDiscoveryElements} />
  );

  const enterpriseMobileHeader = isOrgScoped ? (
    <OrgScopedEnterpriseLearnerOnConsumerMobileMainNav showDiscoveryElements={showDiscoveryElements} />
  ) : (
    <EnterpriseLearnerOnConsumerMobileMainNav showDiscoveryElements={showDiscoveryElements} />
  );

  const desktopNav = (
    <HeaderContextSwitcher
      degreeHeader={<DegreeLearnerOnConsumerDesktopMainNav />}
      enterpriseHeader={enterpriseHeader}
      consumerHeader={<LoggedInConsumerNoSubNavDesktopMainNav showDiscoveryElements={showDiscoveryElements} />}
      loggedOutHeader={<LoggedOutConsumerDesktopMainNav showDiscoveryElements={showDiscoveryElements} />}
      userId={user?.id}
      showDegree={!!chosenDegree}
      showEntProgram={!!chosenEnterpriseProgram}
    />
  );

  const mobileNav = (
    <HeaderContextSwitcher
      degreeHeader={<DegreeLearnerOnConsumerMobileMainNav />}
      enterpriseHeader={enterpriseMobileHeader}
      consumerHeader={<LoggedInConsumerWithMetaNavLinksMobileMainNav showDiscoveryElements={showDiscoveryElements} />}
      loggedOutHeader={<LoggedOutConsumerMobileMainNav showDiscoveryElements={showDiscoveryElements} />}
      userId={user?.id}
      showDegree={!!chosenDegree}
      showEntProgram={!!chosenEnterpriseProgram}
    />
  );

  return (
    <GlobalPageHeaderContainer
      desktopMainNav={desktopNav}
      mobileMainNav={mobileNav}
      metaNav={showMetaNav ? <DefaultMetaNavigation /> : undefined}
      scrollMovementOffset={{
        desktop: showMetaNav ? DEFAULT_META_NAV_HEIGHT : 0,
        mobile: 0,
      }}
      isPositionStatic={isPositionStatic}
      banners={banners}
    />
  );
};

/**
 * This is the default header using the Composable Header pattern. This header is best for when
 * we want learners to easily navigate around the Coursera platform.
 *
 * Note: Do not make direct changes to this header unless explicitly necessary! If you need  more functionality
 * beyond what this header provides, it is encouraged to make a separate header renderer instead.
 */
const DefaultHeaderWithProvider = ({
  banners,
  showMetaNav = !userGet()?.id,
  showDiscoveryElements = true,
  isPositionStatic = false,
}: Props) => {
  const addedHeaderHeight = showMetaNav ? DEFAULT_META_NAV_HEIGHT : 0;
  return (
    <GlobalPageHeaderStateProvider additionalDefaultPageHeaderHeight={addedHeaderHeight}>
      <DefaultConsumerPageHeaderProvider>
        <DefaultHeader
          banners={banners}
          showMetaNav={showMetaNav}
          showDiscoveryElements={showDiscoveryElements}
          isPositionStatic={isPositionStatic}
        />
      </DefaultConsumerPageHeaderProvider>
    </GlobalPageHeaderStateProvider>
  );
};

export default DefaultHeaderWithProvider;
